import React, { useState } from 'react';
import {
    Button,
    Divider,
    Empty,
    Form,
    Input,
    List,
    message,
    notification,
    Spin,
    Typography,
    Row,
    Col,
    Card,
    Table,
} from 'antd';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Money from '../../assets/images/BadgerMoney.svg';
import Request from '../../assets/images/ManageTeam.svg';
import Talking from '../../assets/images/BadgersTeam.svg';
import api from '../../utils/api';
import UserStore from '../../stores/UserStore';
import UIStore from '../../stores/UIStore';
import ErrorRender from '../../components/shared/ErrorRender';
import { FormErrorParser } from '@partsbadger/library';

import NoContactRelatedAlert from '../../components/NoContactRelatedAlert';
import { PageHeader } from 'components/PageHeader';

const { Title, Paragraph, Text } = Typography;

const InvitationForm = (props: {
    onFinish: (values: { first_name: string; last_name: string; email: string }) => Promise<boolean>;
}) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    return (
        <Form
            form={form}
            layout={'vertical'}
            initialValues={{ remember: true }}
            onFinish={async values => {
                setLoading(true);
                const res = await props.onFinish(values);

                if (res) {
                    form.resetFields();
                }

                setLoading(false);
            }}
            onFinishFailed={errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            }}
            autoComplete="off"
        >
            <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: 'Please input a first name.' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: 'Please input a last name.' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item label="Email" name="email" rules={[{ required: true, message: 'Please input an email.' }]}>
                <Input type={'email'} />
            </Form.Item>

            <Form.Item className="mb-0">
                <Button loading={loading} type="primary" htmlType="submit" block={true}>
                    Send Invitation
                </Button>
            </Form.Item>
        </Form>
    );
};

const InviteYourColleagues: React.FC = () => {
    const [references, setReferences] = useState([]);
    const [loading, setLoading] = useState(false);

    const contact_id = UserStore.user?.default_contact?.id;

    UIStore.setSelectedKey(['invite']);
    UIStore.setDocumentTitle('Invite your Colleagues');
    const onSubmit = async (data: { first_name: string; last_name: string; email: string }) => {
        const contact_id = UserStore.user && UserStore.user.default_contact?.id;
        if (!contact_id) {
            return false;
        }

        setLoading(true);

        const payload = [data];

        try {
            await api.Contacts.inviteYourColleages(contact_id, payload);
            loadColleagues(contact_id);
            setLoading(false);
            message.success('Your Invitation has been sent successfully');
            return true;
        } catch (e) {
            setLoading(false);
            notification.error({
                message: 'Error',
                description: <ErrorRender error={e} />,
            });
            return false;
        }
    };

    const loadColleagues = (contact_id: number) => {
        api.Contacts.getAllColleagues(contact_id).then(data => {
            setReferences(data.results.map((i: any) => i.email));
        });
    };
    React.useEffect(() => {
        if (contact_id) {
            loadColleagues(contact_id);
        }
    }, [contact_id]);

    if (!contact_id) {
        return (
            <div>
                <NoContactRelatedAlert />
            </div>
        );
    }

    return (
        <>
            <PageHeader>
                <div className="d-flex flex-column align-items-baseline">
                    <Title level={1}>Invite Your Colleagues</Title>
                    <Paragraph className="mb-0">
                        Allow your entire team access to recent quotes and view all PartsBadger activity with your
                        company in one place.
                    </Paragraph>
                </div>
            </PageHeader>
            <div className="container">
                <div className="d-flex flex-column justify-content-center"></div>

                <Row gutter={[16, 0]}>
                    <Col span={24} md={8}>
                        <Card>
                            <Text italic className="mb-3">
                                Please enter your colleagues's contact information below.
                            </Text>
                            <InvitationForm onFinish={values => onSubmit(values)} />
                        </Card>
                    </Col>
                    <Col span={24} md={16}>
                        <Card title={<Title level={2}>Your Colleagues</Title>} bordered>
                            <Table
                                dataSource={references}
                                pagination={false}
                                columns={[
                                    { title: 'Email' },
                                    {
                                        align: 'right',
                                        render: record => (
                                            <>
                                                <Button
                                                    type="primary"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => {
                                                        api.Contacts.removeColleagueInvitation(
                                                            contact_id,
                                                            record
                                                        ).finally(() => {
                                                            loadColleagues(contact_id);
                                                        });
                                                    }}
                                                    loading={loading}
                                                ></Button>
                                            </>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default InviteYourColleagues;
